import { useRef, useState, type ReactNode } from 'react';
import { Box, Button, Icon, InlineStack, Text } from '@customerio/pluma-components/react';
import clsx from 'clsx';
import { isEmptyString } from '@/utils/string';
import * as styles from './index.css';

export type FileInputProps = {
  /**
   * Called with the first selected/dropped file. `viaDragAndDrop` distinguishes a
   * drop on the container from a pick through the browse button, which otherwise
   * arrive here identically.
   */
  onFileSelected: (file: File, meta: { viaDragAndDrop: boolean }) => void;
  /** Native file type filter, e.g. `.har`. */
  accept?: string;
  isDisabled?: boolean;
  /** Instructional text shown in the dropzone. */
  label?: string;
  /** Text for the browse button. */
  browseLabel?: string;
  /** Error message shown below the dropzone. */
  error?: string;
  /** Additional action shown beside the browse button. */
  action?: ReactNode;
  /** Visual treatment for the input container. */
  variant?: 'dropzone' | 'plain';
  /**
   * Replaces the default browse affordance — e.g. a selected-file display.
   * Drag-and-drop still works while children are shown.
   */
  children?: ReactNode | ((controls: { openFilePicker: () => void }) => ReactNode);
};

/**
 * A presentational drag-and-drop / click-to-browse file input. Pluma has no
 * file-input primitive, so this fills that gap for any page that needs one.
 */
export function FileInput({
  onFileSelected,
  accept,
  isDisabled = false,
  label = 'Drag a file here or browse to upload',
  browseLabel = 'Browse',
  error,
  action,
  variant = 'dropzone',
  children,
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleFile = (files: FileList | null, viaDragAndDrop: boolean) => {
    const file = files?.[0];
    if (file != null) {
      onFileSelected(file, { viaDragAndDrop });
    }
  };

  return (
    <Box>
      <Box
        className={clsx(
          variant === 'dropzone' && styles.dropzone,
          isDragActive && styles.dragActive,
          isDisabled && styles.disabled,
        )}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragActive(false);
          if (!isDisabled) {
            handleFile(event.dataTransfer.files, true);
          }
        }}
      >
        {/* oxlint-disable-next-line pluma/enforce-pluma-elements -- native file input is required for browser file picker APIs. */}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={isDisabled}
          aria-label={label}
          className={styles.hiddenInput}
          onChange={(event) => {
            handleFile(event.target.files, false);
            // Allow re-selecting the same file.
            event.target.value = '';
          }}
        />
        {typeof children === 'function'
          ? children({ openFilePicker: () => inputRef.current?.click() })
          : (children ?? (
              <>
                <Icon name="upload" size="lg" color="subtle" />
                <Text color="subtle">{label}</Text>
                <InlineStack alignBlock="center" alignInline="center" gap="100" wrap={true}>
                  <Button variant="secondary" isDisabled={isDisabled} onClick={() => inputRef.current?.click()}>
                    {browseLabel}
                  </Button>
                  {action}
                </InlineStack>
              </>
            ))}
      </Box>
      {!isEmptyString(error) ? (
        <Text color="critical" size="xs" mt="050">
          {error}
        </Text>
      ) : null}
    </Box>
  );
}
